import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";
import { clearMasterCache } from "@/lib/masterCache";

const AuthCtx = createContext(null);

// 2026-08-25 · Idle-timeout policy.
// Any authenticated tab that goes 30 minutes without a user event
// (mousemove / keydown / click / touch / scroll / visibilitychange
// coming back to visible) is force-logged-out and bounced to
// /login?reason=idle_timeout. Timer resets on each event via a
// throttled handler so keystroke-heavy pages don't burn CPU.
const IDLE_TIMEOUT_MS   = 30 * 60 * 1000;
const IDLE_ACTIVITY_EVS = [
  "mousemove", "mousedown", "keydown", "touchstart",
  "scroll", "wheel", "visibilitychange",
];
const IDLE_RESET_THROTTLE_MS = 15 * 1000;   // reset the timer at most every 15s

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [error, setError] = useState("");
  // Run-C: surface 429 lockout state so the login page can show a clear message.
  const [rateLimit, setRateLimit] = useState(null); // { retryAfter: int seconds, message: str }

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data?.data?.user || false);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    setRateLimit(null);
    try {
      // Perf B1 (2026-03-01): /auth/login already returns the exact
      // same enriched user shape as /auth/me (roles, permissions,
      // enabled_modules, plan_tier, primary_role, default_dashboard_route,
      // scope_type, is_owner, …). Consume it directly and skip the
      // redundant follow-up round-trip. Browser-refresh + explicit
      // session-restore paths still call /auth/me via fetchMe().
      const { data } = await api.post("/auth/login", { email, password });
      const loggedIn = data?.data?.user;
      if (loggedIn) {
        setUser(loggedIn);
      } else {
        // Defensive fallback if a future backend change drops the
        // enriched user from the login body — keep the flow correct
        // by refetching. Won't fire in current implementation.
        await fetchMe();
      }
      return true;
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      if (status === 429) {
        // Prefer envelope details, fall back to Retry-After header.
        const detailsRetry = body?.errors?.[0]?.details?.retry_after_seconds;
        const headerRetry = parseInt(e?.response?.headers?.["retry-after"] || "0", 10);
        const retryAfter = Number(detailsRetry) || headerRetry || 0;
        const msg = body?.errors?.[0]?.message || "Too many login attempts. Please try again later.";
        setRateLimit({ retryAfter, message: msg });
        setError("");
      } else {
        // Phase TENANT-SUSPEND — preserve the backend error `code` on the
        // context error so the Login page can differentiate
        // TENANT_SUSPENDED from ACCOUNT_DISABLED / INVALID_CREDENTIALS.
        // 2026-08-25 · Fall through to `detail.code` too — the
        // subscription validity gate (`SUBSCRIPTION_EXPIRED`) raises
        // via HTTPException, which surfaces under `body.detail`.
        const code = body?.errors?.[0]?.code
                   || (body?.detail && typeof body.detail === "object" ? body.detail.code : null)
                   || null;
        const msg  = body?.errors?.[0]?.message
                   || (body?.detail && typeof body.detail === "object" ? body.detail.message : null)
                   || formatErr(body)
                   || e.message;
        setError({ code, message: msg });
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    clearMasterCache();
    setUser(false);
  };

  // ── 2026-08-25 · Idle-timeout enforcement ──────────────────────────
  //
  // Runs only when a real user is logged in. On expiry we clear the
  // server session (best-effort) + local state + hard-redirect to
  // /login?reason=idle_timeout so the friendly banner shows.
  //
  // `lastResetRef` throttles the reset burst on high-frequency events.
  const idleTimerRef  = useRef(null);
  const lastResetRef  = useRef(0);

  useEffect(() => {
    // Skip while user status is unknown or user is anonymous.
    if (!user || typeof window === "undefined") return undefined;

    const clearTimer = () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const expireNow = async () => {
      clearTimer();
      try { await api.post("/auth/logout"); } catch (_) { /* best effort */ }
      try { sessionStorage.setItem("sessionExpiredNotice", "idle"); } catch (_) { /* ignore */ }
      clearMasterCache();
      setUser(false);
      if (typeof window !== "undefined") {
        const path = window.location.pathname || "";
        if (!path.startsWith("/login")) {
          window.location.assign("/login?reason=idle_timeout");
        }
      }
    };

    const scheduleExpiry = () => {
      clearTimer();
      idleTimerRef.current = setTimeout(expireNow, IDLE_TIMEOUT_MS);
    };

    const onActivity = (e) => {
      // `visibilitychange` fires both ways — only reset when the tab
      // becomes visible again (otherwise a hidden tab could reset the
      // timer indefinitely with no user present).
      if (e?.type === "visibilitychange" && document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastResetRef.current < IDLE_RESET_THROTTLE_MS) return;
      lastResetRef.current = now;
      scheduleExpiry();
    };

    // Prime the timer, wire the events.
    scheduleExpiry();
    IDLE_ACTIVITY_EVS.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true }),
    );
    return () => {
      clearTimer();
      IDLE_ACTIVITY_EVS.forEach((ev) =>
        window.removeEventListener(ev, onActivity),
      );
    };
  }, [user]);

  const can = (key) => {
    if (!user) return false;
    if ((user.roles || []).includes("Owner") || (user.roles || []).includes("Admin")) return true;
    return (user.permissions || []).includes(key);
  };

  // Phase HR-1A — HR-sensitive permission helper.
  // Behaviour deliberately diverges from `can()`:
  //   • Client User is always denied — HR is internal-only.
  //   • Owner passes when `ownerAllowed=true` (the default).
  //   • Admin does NOT auto-pass — payroll / salary data must stay
  //     out of the ordinary Admin scope. Admin can be granted HR
  //     access by assigning the "HR Manager" role on top of Admin.
  //   • Otherwise checks the user's explicit permission list.
  // Use `canHR()` for HR & Payroll / My HR navigation and page
  // visibility; the backend HR helper is the actual security
  // boundary (see `core/hr_permissions.py`).
  const canHR = (key, { ownerAllowed = true } = {}) => {
    if (!user) return false;
    const roles = user.roles || [];
    if (roles.includes("Client User")) return false;
    if (ownerAllowed && roles.includes("Owner")) return true;
    // Admin must not auto-pass. We approximate the backend rule by
    // requiring the user to ALSO carry an explicit non-Admin role
    // grant. Without per-role permission visibility in the JWT, we
    // simply deny Admin-only users for HR keys.
    if (roles.includes("Admin") && !roles.includes("Owner")
        && !roles.includes("HR Manager") && !roles.includes("Employee")) {
      return false;
    }
    return (user.permissions || []).includes(key);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, error, rateLimit, can, canHR, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
